import { _ as __vitePreload } from "main/modulepreload-polyfill";

/* ============================================================
   ✅ FIX: CART FREEZE AFTER RETURNING FROM CHECKOUT (bfcache)
============================================================ */
window.addEventListener("pageshow", (event) => {
  if (event.persisted) {
    window.location.reload();
  }
});

/* ============================================================
   ✅ SHOPIFY CART ADD FIX — NORMALIZE /cart/add(.js) REQUESTS
============================================================ */
(function patchCartAddRequests() {
  const alreadyPatched = window.__dsCartAddPatched;
  if (alreadyPatched) return;
  window.__dsCartAddPatched = true;

  const isCartAddUrl = (url) => {
    try {
      const u =
        typeof url === "string"
          ? new URL(url, window.location.origin)
          : new URL(url.url, window.location.origin);
      return /\/cart\/add(\.js)?$/i.test(u.pathname);
    } catch (e) {
      return (
        typeof url === "string" && /\/cart\/add(\.js)?(\?|$)/i.test(url)
      );
    }
  };

  const isPlainObject = (v) =>
    v &&
    typeof v === "object" &&
    (v.constructor === Object ||
      Object.getPrototypeOf(v) === Object.prototype);

  const toInt = (v) => {
    if (v == null) return null;
    if (typeof v === "number") return v;
    if (typeof v === "string" && v.trim() !== "") {
      const n = parseInt(v, 10);
      return Number.isNaN(n) ? null : n;
    }
    return null;
  };

  const normalizeItem = (item) => {
    if (!item || typeof item !== "object") return null;
    const idRaw = item.id ?? item.variantId ?? item.variant_id ?? item.variantID;
    const qtyRaw = item.quantity ?? item.qty ?? 1;
    const id = toInt(idRaw);
    const quantity = toInt(qtyRaw) || 1;
    if (!id) return null;

    const out = { id, quantity };
    if (item.properties && typeof item.properties === "object")
      out.properties = item.properties;
    if (item.selling_plan) out.selling_plan = item.selling_plan;
    return out;
  };

  const buildUrlEncodedBody = (payload) => {
    const params = new URLSearchParams();

    if (payload && Array.isArray(payload.items)) {
      const items = payload.items.map(normalizeItem).filter(Boolean);
      if (!items.length) return null;

      items.forEach((it, idx) => {
        params.set(`items[${idx}][id]`, String(it.id));
        params.set(`items[${idx}][quantity]`, String(it.quantity));

        if (it.properties) {
          Object.entries(it.properties).forEach(([k, v]) => {
            params.set(`items[${idx}][properties][${k}]`, String(v));
          });
        }

        if (it.selling_plan) {
          params.set(`items[${idx}][selling_plan]`, String(it.selling_plan));
        }
      });

      return params.toString();
    }

    const one = normalizeItem(payload);
    if (!one) return null;

    params.set("id", String(one.id));
    params.set("quantity", String(one.quantity));

    if (one.properties) {
      Object.entries(one.properties).forEach(([k, v]) => {
        params.set(`properties[${k}]`, String(v));
      });
    }

    if (one.selling_plan) {
      params.set("selling_plan", String(one.selling_plan));
    }

    return params.toString();
  };

  const tryParseJson = (str) => {
    if (typeof str !== "string") return null;
    const s = str.trim();
    if (!s || (s[0] !== "{" && s[0] !== "[")) return null;
    try {
      return JSON.parse(s);
    } catch (e) {
      return null;
    }
  };

  const ensureAcceptHeader = (headers) => {
    const h = new Headers(headers || {});
    if (!h.has("Accept")) h.set("Accept", "application/json");
    return h;
  };

  if (window.fetch) {
    const originalFetch = window.fetch.bind(window);

    window.fetch = function patchedFetch(input, init = {}) {
      try {
        if (!isCartAddUrl(input)) return originalFetch(input, init);

        const nextInit = { ...init };
        nextInit.method = (nextInit.method || "POST").toUpperCase();

        let normalizedBodyString = null;

        if (nextInit.body instanceof FormData) {
          nextInit.headers = ensureAcceptHeader(nextInit.headers);
          return originalFetch(input, nextInit);
        }

        if (nextInit.body instanceof URLSearchParams) {
          normalizedBodyString = nextInit.body.toString();
        } else if (typeof nextInit.body === "string") {
          const parsed = tryParseJson(nextInit.body);
          normalizedBodyString = parsed
            ? buildUrlEncodedBody(parsed)
            : nextInit.body;
        } else if (isPlainObject(nextInit.body)) {
          normalizedBodyString = buildUrlEncodedBody(nextInit.body);
        }

        if (!normalizedBodyString) return originalFetch(input, init);

        const headers = new Headers(nextInit.headers || {});
        headers.set("Accept", "application/json");
        headers.set(
          "Content-Type",
          "application/x-www-form-urlencoded; charset=UTF-8"
        );
        nextInit.headers = headers;
        nextInit.body = normalizedBodyString;

        return originalFetch(input, nextInit);
      } catch (e) {
        return originalFetch(input, init);
      }
    };
  }
})();

/* ============================================================
   ✅ CART CHANGE FAILSAFE — ONLY FOR /cart/change|update|clear
   (DO NOT trap global unhandled promise rejections)
============================================================ */
(function patchCartChangeFailsafe() {
  if (window.__dsCartChangeFailsafePatched) return;
  window.__dsCartChangeFailsafePatched = true;

  const isCartChangeUrl = (input) => {
    try {
      const u =
        typeof input === "string"
          ? new URL(input, window.location.origin)
          : new URL(input.url, window.location.origin);
      return /\/cart\/(change|update|clear)(\.js)?$/i.test(u.pathname);
    } catch (e) {
      return (
        typeof input === "string" &&
        /\/cart\/(change|update|clear)(\.js)?(\?|$)/i.test(input)
      );
    }
  };

  const hardRecover = () => {
    window.location.href = "/cart";
  };

  if (!window.fetch) return;

  const originalFetch = window.fetch.bind(window);

  window.fetch = async function patchedFetch(input, init) {
    const isChange = isCartChangeUrl(input);

    try {
      const res = await originalFetch(input, init);

      // Only force recover if the request was a cart change AND failed
      if (isChange && !res.ok) hardRecover();

      return res;
    } catch (err) {
      // Only force recover if the request was a cart change AND threw
      if (isChange) hardRecover();
      throw err;
    }
  };
})();

/* ============================================================
   MEDIA / VISIBILITY HELPERS
============================================================ */
function media({ query }) {
  const mediaQuery = window.matchMedia({ query }.query);
  return new Promise(function (resolve) {
    if (mediaQuery.matches) {
      resolve(true);
    } else {
      mediaQuery.addEventListener("change", resolve, { once: true });
    }
  });
}

function visible({ element }) {
  return new Promise(function (resolve) {
    const observer = new window.IntersectionObserver(async function (entries) {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          observer.disconnect();
          resolve(true);
          break;
        }
      }
    });
    observer.observe(element);
  });
}

function idle() {
  return Promise.resolve(true);
}

/* ============================================================
   ISLAND REGISTRY
============================================================ */
const islands = Object.assign({
  // Existing (Cart + Header)
  "/frontend/islands/cart-agreement.js": () =>
    __vitePreload(() => import("components/cart-agreement"), true ? [] : void 0, import.meta.url),
  "/frontend/islands/cart-discount.js": () =>
    __vitePreload(() => import("components/cart-discount"), true ? [] : void 0, import.meta.url),
  "/frontend/islands/cart-drawer-items.js": () =>
    __vitePreload(() => import("components/cart-drawer-items"), true ? [] : void 0, import.meta.url),
  "/frontend/islands/cart-drawer.js": () =>
    __vitePreload(() => import("components/cart-drawer"), true ? [] : void 0, import.meta.url),
  "/frontend/islands/cart-items.js": () =>
    __vitePreload(() => import("components/cart-items"), true ? [] : void 0, import.meta.url),
  "/frontend/islands/cart-note.js": () =>
    __vitePreload(() => import("components/cart-note"), true ? [] : void 0, import.meta.url),
  "/frontend/islands/cart-notification.js": () =>
    __vitePreload(() => import("components/cart-notification"), true ? [] : void 0, import.meta.url),
  "/frontend/islands/cart-remove-button.js": () =>
    __vitePreload(() => import("components/cart-remove-button"), true ? [] : void 0, import.meta.url),
  "/frontend/islands/header-section.js": () =>
    __vitePreload(() => import("components/header-section"), true ? [] : void 0, import.meta.url),

  // ✅ ADD (Collection / Filters)
  "/frontend/islands/collection-drawer.js": () =>
    __vitePreload(() => import("components/collection-drawer"), true ? [] : void 0, import.meta.url),
  "/frontend/islands/collection-sort.js": () =>
    __vitePreload(() => import("components/collection-sort"), true ? [] : void 0, import.meta.url),
  "/frontend/islands/filter-button.js": () =>
    __vitePreload(() => import("components/filter-button"), true ? [] : void 0, import.meta.url),
  "/frontend/islands/filter-remove.js": () =>
    __vitePreload(() => import("components/filter-remove"), true ? [] : void 0, import.meta.url),
  "/frontend/islands/facet-filters-form.js": () =>
    __vitePreload(() => import("components/facet-filters-form"), true ? [] : void 0, import.meta.url),
});

/* ============================================================
   ISLAND REVIVER
============================================================ */
function revive(islands2) {
  const observer = new window.MutationObserver((mutations) => {
    for (let i = 0; i < mutations.length; i++) {
      const { addedNodes } = mutations[i];
      for (let j = 0; j < addedNodes.length; j++) {
        const node = addedNodes[j];
        if (node.nodeType === 1) dfs(node);
      }
    }
  });

  async function dfs(node) {
    const tagName = node.tagName.toLowerCase();
    const potentialJsPath = `/frontend/islands/${tagName}.js`;
    const isPotentialCustomElementName = /-/.test(tagName);

    if (isPotentialCustomElementName && islands2[potentialJsPath]) {
      if (node.hasAttribute("client:visible")) await visible({ element: node });
      const clientMedia = node.getAttribute("client:media");
      if (clientMedia) await media({ query: clientMedia });
      if (node.hasAttribute("client:idle")) await idle();
      islands2[potentialJsPath]();
    }

    let child = node.firstElementChild;
    while (child) {
      dfs(child);
      child = child.nextElementSibling;
    }
  }

  dfs(document.body);
  observer.observe(document.body, { childList: true, subtree: true });
}

revive(islands);